import React, { useState } from "react";
import * as procService from "../../services/procService";
import MasterSection from "../Platform/setup/MasterSection";
import AccessSetup from "../Platform/setup/AccessSetup";
import NumberingSetup from "../Platform/setup/NumberingSetup";
import WorkflowSetup from "../Platform/setup/WorkflowSetup";
import { DetailGrid, FormDrawer, LoadState, PageHeader, Tabs, cardStyle, iconBtnStyle, pageStyle, useLoad } from "../../components/ui";

// Procurement setup (Procurement admin): System Options, the documents suppliers are asked
// for, document numbering, award approvals and who can do what.
const linkBtn = { ...iconBtnStyle, fontSize: 13, fontWeight: 600 };

export const RFQ_TYPE_LABELS = { LUMPSUM: "Lumpsum (whole RFQ to one supplier)", ITEM: "Item based (each item to one supplier)", SPLIT: "Split (an item shared between suppliers)" };
const RFQ_TYPE_OPTIONS = Object.entries(RFQ_TYPE_LABELS).map(([value, label]) => ({ value, label }));

export function SystemOptions() {
  const { data, loading, error, reload } = useLoad(() => procService.getSettings(), []);
  const warehouses = useLoad(() => procService.getWarehouses(), []);
  const [editing, setEditing] = useState(false);
  const s = data || {};
  const warehouseOptions = (warehouses.data || []).map((w) => ({ label: `${w.code} – ${w.name}`, value: w.id, code: w.code }));
  return (
    <LoadState loading={loading} error={error} onRetry={reload}>
      <div style={{ ...cardStyle, padding: 20, maxWidth: 720 }}>
        <div className="d-flex justify-content-between mb-3">
          <h6 className="mb-0">System options</h6>
          <button type="button" style={linkBtn} onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
        <DetailGrid
          items={[
            ["Default RFQ type", RFQ_TYPE_LABELS[s.default_rfq_type]],
            ["Smallest share in a split award", s.default_min_split_pct !== undefined ? `${Number(s.default_min_split_pct)}%` : null],
            ["Suppliers have to respond within", s.default_response_days ? `${s.default_response_days} days` : null],
            ["Delivery warehouse", s.default_warehouse_code ? `${s.default_warehouse_code} – ${s.default_warehouse_name}` : "The default warehouse"],
            ["Draft a supplier bill when goods are received", s.auto_ap_draft ? "Yes (when Finance is on)" : "No"],
          ]}
        />
      </div>
      <FormDrawer
        show={editing}
        title="System options"
        // The warehouse is matched by its code: ids are encrypted afresh in every response.
        initial={{ ...s, default_warehouse_id: warehouseOptions.find((w) => w.code === s.default_warehouse_code)?.value ?? null }}
        onHide={() => setEditing(false)}
        onSave={(clean, raw) =>
          procService.saveSettings({
            default_rfq_type: clean.default_rfq_type,
            default_min_split_pct: Number(clean.default_min_split_pct),
            default_response_days: Number(clean.default_response_days),
            default_warehouse_id: clean.default_warehouse_id || null,
            auto_ap_draft: !!raw.auto_ap_draft,
          })
        }
        onDone={reload}
        fields={[
          { key: "default_rfq_type", label: "Default RFQ type", type: "select", required: true, options: RFQ_TYPE_OPTIONS },
          { key: "default_min_split_pct", label: "Smallest share in a split award (%)", type: "number", step: "any", min: 0, required: true },
          { key: "default_response_days", label: "Days suppliers have to respond", type: "number", min: 1, required: true },
          { key: "default_warehouse_id", label: "Delivery warehouse", type: "select", options: warehouseOptions, hint: "Leave empty to use the default warehouse" },
          { key: "auto_ap_draft", label: "Draft a supplier bill when goods are received (needs Finance)", type: "checkbox" },
        ]}
      />
    </LoadState>
  );
}

const KIND_LABEL = { COMPANY: "About the supplier", RFQ: "With a quotation" };

export function DocumentTypes() {
  return (
    <MasterSection
      noun="document type"
      emptyText="Documents suppliers are asked for: trade licence, tax certificate, test certificates ..."
      load={() => procService.listMaster("document_types")}
      save={(id, data) => {
        const { sort_order: order, ...rest } = data;
        const body = order === null || order === undefined || order === "" ? rest : { ...rest, sort_order: Number(order) };
        return procService.saveMaster("document_types", id, body);
      }}
      columns={[
        ["Code", (r) => r.code],
        ["Name", (r) => r.name],
        ["Asked for", (r) => KIND_LABEL[r.applies_to] || r.applies_to],
        ["Mandatory", (r) => (r.is_mandatory ? "Yes" : "No")],
        ["Expiry date", (r) => (r.has_expiry ? "Yes" : "No")],
      ]}
      toForm={(r) => ({ ...r, is_mandatory: !!r.is_mandatory, has_expiry: !!r.has_expiry })}
      defaults={{ applies_to: "COMPANY", is_mandatory: false, has_expiry: false }}
      fieldKeys={["name", "applies_to", "is_mandatory", "has_expiry", "sort_order"]}
      lockedOnEdit={["code"]}
      fields={() => [
        { key: "code", label: "Code", required: true, max: 32, hint: "Letters, digits, - and _" },
        { key: "name", label: "Name", required: true, max: 100 },
        { key: "applies_to", label: "Asked for", type: "select", required: true, options: [{ value: "COMPANY", label: KIND_LABEL.COMPANY }, { value: "RFQ", label: KIND_LABEL.RFQ }] },
        { key: "is_mandatory", label: "Mandatory", type: "checkbox" },
        { key: "has_expiry", label: "Has an expiry date", type: "checkbox" },
        { key: "sort_order", label: "Order in the list", type: "number", min: 0 },
      ]}
    />
  );
}

const TABS = [
  { key: "options", label: "System options", render: () => <SystemOptions /> },
  { key: "documents", label: "Supplier documents", render: () => <DocumentTypes /> },
  { key: "numbering", label: "Document numbering", render: () => <NumberingSetup module="proc" wholeCompanyOnly /> },
  { key: "approvals", label: "Approvals", render: () => <WorkflowSetup module="proc" /> },
  { key: "access", label: "Access", render: () => <AccessSetup module="proc" label="Procurement" showScopes={false} /> },
];

export default function ProcSetup() {
  const [tab, setTab] = useState("options");
  const active = TABS.find((t) => t.key === tab);
  return (
    <div style={pageStyle}>
      <PageHeader title="Procurement Setup" subtitle="Defaults for RFQs, the documents suppliers are asked for, numbering, award approvals and access" />
      <Tabs tabs={TABS.map(({ key, label }) => ({ key, label }))} active={tab} onChange={setTab} />
      {active.render()}
    </div>
  );
}
